/**
 * Hook para gerenciar variáveis globais do tenant
 * Permite criar, listar, atualizar e deletar variáveis compartilhadas entre fluxos
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useTenantVariables');

export interface TenantVariable {
  id: string;
  tenant_id: string;
  name: string;
  display_name: string | null;
  type: 'text' | 'number' | 'boolean' | 'date' | 'email' | 'phone' | 'cpf' | 'cnpj';
  description: string | null;
  default_value: string | null;
  is_system: boolean;
  category: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateVariableInput {
  name: string;
  display_name?: string;
  type?: TenantVariable['type'];
  description?: string;
  default_value?: string;
}

export interface UpdateVariableInput {
  display_name?: string;
  type?: TenantVariable['type'];
  description?: string;
  default_value?: string;
}

/**
 * Hook principal para gerenciar variáveis do tenant
 */
export function useTenantVariables(tenantId?: string) {
  const queryClient = useQueryClient();

  // Busca todas as variáveis do tenant
  const { data: variables, isLoading, error, refetch } = useQuery({
    queryKey: ['tenant-variables', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      log.debug('Buscando variáveis do tenant:', tenantId);

      const { data, error } = await supabase
        .from('tenant_variables')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('is_system', { ascending: false })
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        log.error('Erro ao buscar variáveis:', error);
        throw error;
      }

      log.debug(`${data.length} variáveis encontradas`);
      return data as TenantVariable[];
    },
    enabled: !!tenantId,
  });

  // Criar nova variável
  const createVariable = useMutation({
    mutationFn: async (input: CreateVariableInput) => {
      if (!tenantId) throw new Error('Tenant ID não fornecido');

      log.debug('Criando variável:', input);

      const { data: user } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('tenant_variables')
        .insert({
          tenant_id: tenantId,
          name: input.name,
          display_name: input.display_name || input.name,
          type: input.type || 'text',
          description: input.description,
          default_value: input.default_value,
          created_by: user.user?.id,
        })
        .select()
        .single();

      if (error) {
        log.error('Erro ao criar variável:', error);
        throw error;
      }

      log.info('Variável criada com sucesso:', data);
      return data as TenantVariable;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-variables', tenantId] });
      toast.success('Variável criada com sucesso');
    },
    onError: (error: any) => {
      log.error('Erro na mutation de criar variável:', error);
      
      if (error.code === '23505') {
        toast.error('Já existe uma variável com esse nome');
      } else {
        toast.error('Erro ao criar variável');
      }
    },
  });

  // Atualizar variável existente
  const updateVariable = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateVariableInput }) => {
      log.debug('Atualizando variável:', { id, input });

      const { data, error } = await supabase
        .from('tenant_variables')
        .update({
          display_name: input.display_name,
          type: input.type,
          description: input.description,
          default_value: input.default_value,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        log.error('Erro ao atualizar variável:', error);
        throw error;
      }

      log.info('Variável atualizada com sucesso:', data);
      return data as TenantVariable;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-variables', tenantId] });
      toast.success('Variável atualizada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao atualizar variável');
    },
  });

  // Deletar variável
  const deleteVariable = useMutation({
    mutationFn: async (id: string) => {
      log.debug('Deletando variável:', id);

      const { error } = await supabase
        .from('tenant_variables')
        .delete()
        .eq('id', id);

      if (error) {
        log.error('Erro ao deletar variável:', error);
        throw error;
      }

      log.info('Variável deletada com sucesso');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-variables', tenantId] });
      toast.success('Variável deletada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao deletar variável');
    },
  });

  return {
    variables: variables || [],
    isLoading,
    error,
    refetch,
    createVariable: createVariable.mutate,
    createVariableAsync: createVariable.mutateAsync,
    updateVariable: updateVariable.mutate,
    deleteVariable: deleteVariable.mutate,
    isCreating: createVariable.isPending,
    isUpdating: updateVariable.isPending,
    isDeleting: deleteVariable.isPending,
  };
}